import { Link } from 'react-router-dom';
import { Target, Eye, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative bg-[#1B2A1B] py-20">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1627306036351-036986f292a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxCYW5nYWxvcmUlMjBXaGl0ZWZpZWxkJTIwY2l0eXNjYXBlJTIwYnVpbGRpbmdzfGVufDB8fHx8MTc3NjMyODUyNXww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">About Us</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-semibold text-white tracking-tight mb-4">
            Our Story
          </h1>
          <p className="text-lg text-olive-300 max-w-2xl mx-auto">
            Established in Whitefield, Bengaluru, SVT & Co has been a cornerstone of tax consultancy excellence for over 20 years.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">Who We Are</p>
            <h2 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight mb-6">
              Bengaluru's Trusted Tax Consultancy
            </h2>
            <div className="space-y-4 text-sm text-[#3B5A3B] leading-relaxed">
              <p>
                SVT & Co was founded with a simple mission: to make tax compliance effortless for businesses
                and individuals. Based in the heart of Whitefield, Bengaluru's thriving IT corridor, we've
                grown from a small practice to a full-service tax consultancy managing over 200 clients.
              </p>
              <p>
                Under the leadership of <strong className="text-[#111B11]">Sathisha M M R</strong>, our team of
                experienced professionals brings deep expertise in Income Tax, GST, Professional Tax,
                company registrations, and business advisory services.
              </p>
              <p>
                We pride ourselves on our personalized approach — every client receives dedicated attention,
                transparent pricing, and timely service. Our technology-driven processes ensure zero-penalty
                filing records and real-time status updates for all our clients.
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1758518726869-01aff69a56e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwY29ycG9yYXRlJTIwbW9kZXJufGVufDB8fHx8MTc3NjMyODUxNXww&ixlib=rb-4.1.0&q=85"
              alt="SVT & Co team"
              className="rounded-sm shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="bg-[#F4F7F4] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Our Mission', text: 'To simplify tax compliance and empower businesses with accurate, timely, and transparent financial services that drive growth and ensure peace of mind.' },
              { icon: Eye, title: 'Our Vision', text: 'To be Bengaluru\'s most trusted tax consultancy, recognized for excellence, integrity, and client-first approach in every engagement.' },
              { icon: Heart, title: 'Our Values', text: 'Integrity in every transaction. Transparency in every communication. Excellence in every filing. Client success is our success.' },
            ].map(item => (
              <div key={item.title} className="bg-white border border-[#E6EDE6] rounded-sm p-8">
                <div className="w-12 h-12 rounded-sm bg-[#1B2A1B] flex items-center justify-center mb-5">
                  <item.icon size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-[#111B11] mb-3">{item.title}</h3>
                <p className="text-sm text-[#3B5A3B] leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Summary */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">Our Expertise</p>
          <h2 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Areas of Practice</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {['Income Tax', 'GST Returns', 'Professional Tax', 'Company Registration', 'MSME Filing', 'Tax Planning'].map(area => (
            <div key={area} className="bg-[#1B2A1B] rounded-sm p-5 text-center hover:bg-[#2F472F] transition-colors">
              <p className="text-sm font-medium text-[#D4AF37]">{area}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#D4AF37] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1B2A1B] tracking-tight mb-4">
            Let's Work Together
          </h2>
          <p className="text-sm text-[#1B2A1B]/70 mb-8 max-w-lg mx-auto">
            Whether you're an individual taxpayer or a growing business, we have the expertise to help you stay compliant and grow.
          </p>
          <Link to="/svt-co/contact">
            <Button className="h-12 px-8 bg-[#1B2A1B] text-white hover:bg-[#2F472F] font-semibold rounded-sm gap-2" data-testid="about-cta-btn">
              Get In Touch <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
